import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Search, Heart, ShoppingBag, User as UserIcon, LogOut, Flame } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

export default function Header() {
  const { user, logout, cart, wishlist } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [sugg, setSugg] = useState([]);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => { setQ(""); setSugg([]); }, [loc.pathname]);

  useEffect(() => {
    if (!q.trim()) { setSugg([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products/suggest?q=${encodeURIComponent(q)}`);
        setSugg(data);
      } catch (_e) {
        // suggestions are best-effort; silently drop on network/abort errors
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const cartCount = (cart?.items || []).reduce((a, i) => a + (i.quantity || 0), 0);

  const submit = (e) => { e.preventDefault(); if (q.trim()) nav(`/products?q=${encodeURIComponent(q.trim())}`); };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2 shrink-0">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#004BCA] text-white">
            <Flame size={18} strokeWidth={2} />
          </span>
          <span className="font-display font-bold text-xl tracking-tight">
            Style<span className="text-[#FF4500]">fire</span>
          </span>
        </Link>

        <form ref={ref} onSubmit={submit} className="relative flex-1 max-w-xl hidden md:block">
          <div className="flex items-center bg-slate-100 rounded-full px-4 h-11 focus-within:ring-2 focus-within:ring-[#004BCA]/30">
            <Search size={18} className="text-slate-500" strokeWidth={1.7} />
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Search for dresses, sneakers, brands…"
              className="bg-transparent flex-1 px-3 outline-none text-sm"
            />
          </div>
          {open && sugg.length > 0 && (
            <div className="absolute top-12 left-0 right-0 bg-white rounded-2xl shadow-2xl border border-slate-100 py-2 max-h-96 overflow-auto">
              {sugg.map(s => (
                <Link key={s.product_id} to={`/product/${s.product_id}`} className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50">
                  <img src={s.images?.[0]} alt={s.title} className="w-10 h-10 rounded-lg object-cover" />
                  <div className="flex-1">
                    <div className="text-sm font-medium truncate">{s.title}</div>
                    <div className="text-xs text-slate-500">₹{s.price}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </form>

        <nav className="flex items-center gap-2 ml-auto">
          <Link to="/wishlist" data-testid="header-wishlist" className="relative grid place-items-center w-10 h-10 rounded-full hover:bg-slate-100">
            <Heart size={20} strokeWidth={1.7} />
            {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#FF4500] text-white text-[10px] rounded-full w-5 h-5 grid place-items-center">{wishlist.length}</span>}
          </Link>
          <Link to="/cart" data-testid="header-cart" className="relative grid place-items-center w-10 h-10 rounded-full hover:bg-slate-100">
            <ShoppingBag size={20} strokeWidth={1.7} />
            {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#004BCA] text-white text-[10px] rounded-full w-5 h-5 grid place-items-center">{cartCount}</span>}
          </Link>

          {!user ? (
            <Link to="/login" data-testid="header-login-btn" className="ml-2 hidden sm:inline-flex items-center gap-2 bg-[#004BCA] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#003ea8]">
              <UserIcon size={16} strokeWidth={2} /> Login
            </Link>
          ) : (
            <div className="relative">
              <button
                data-testid="header-user-menu"
                onClick={() => setMenuOpen(v => !v)}
                className="ml-2 inline-flex items-center gap-2 px-3 py-2 rounded-full hover:bg-slate-100 text-sm">
                <span className="grid place-items-center w-7 h-7 rounded-full bg-[#004BCA] text-white text-xs">{(user.name || user.email)?.[0]?.toUpperCase()}</span>
                <span className="hidden sm:inline font-medium">{user.name?.split(" ")[0] || "Account"}</span>
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-12 w-56 bg-white rounded-2xl shadow-2xl border border-slate-100 py-2 z-50" onMouseLeave={() => setMenuOpen(false)}>
                  <div className="px-4 py-2 border-b border-slate-100">
                    <div className="text-sm font-semibold truncate">{user.name}</div>
                    <div className="text-xs text-slate-500 truncate">{user.email}</div>
                  </div>
                  <Link data-testid="menu-profile" to="/account/profile" className="block px-4 py-2 hover:bg-slate-50 text-sm">My Profile</Link>
                  <Link data-testid="menu-orders" to="/account/orders" className="block px-4 py-2 hover:bg-slate-50 text-sm">My Orders</Link>
                  <Link data-testid="menu-wishlist" to="/wishlist" className="block px-4 py-2 hover:bg-slate-50 text-sm">Wishlist</Link>
                  <Link data-testid="menu-addresses" to="/account/addresses" className="block px-4 py-2 hover:bg-slate-50 text-sm">Saved Addresses</Link>
                  <Link data-testid="menu-settings" to="/account/settings" className="block px-4 py-2 hover:bg-slate-50 text-sm">Account Settings</Link>
                  {user.role === "admin" && <Link data-testid="menu-admin" to="/admin" className="block px-4 py-2 hover:bg-slate-50 text-sm font-medium text-[#004BCA]">Admin Panel</Link>}
                  <button data-testid="menu-logout" onClick={async () => { await logout(); nav("/"); }} className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm text-red-600 inline-flex items-center gap-2">
                    <LogOut size={14} /> Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </nav>
      </div>

      {/* mobile search */}
      <div className="md:hidden px-4 pb-3">
        <form onSubmit={submit} className="flex items-center bg-slate-100 rounded-full px-4 h-10">
          <Search size={16} className="text-slate-500" />
          <input data-testid="header-search-input-mobile" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Stylefire…" className="bg-transparent flex-1 px-3 outline-none text-sm" />
        </form>
      </div>
    </header>
  );
}
